import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import "../styles/withdraw.css";

const NETWORK = "TRC20";
const TOKEN = "USDT";
const MIN_WITHDRAW = 50;
const NETWORK_FEE_PERCENT = 10;
const EARLY_SETTLEMENT_FEE_PERCENT = 20;
const EARLY_WITHDRAW_DAYS = 30;
const API_BASE_URL = "https://primerise.space";

export default function Withdraw() {
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const getToken = () => {
    return (
      localStorage.getItem("token") || localStorage.getItem("naxoraToken") || ""
    );
  };

  const fetchProfile = async () => {
    try {
      setLoadingProfile(true);

      const token = getToken();

      if (!token) {
        throw new Error("Login required");
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.user) {
        throw new Error(data.message || "Unable to load profile");
      }

      setUserProfile(data.user);
    } catch (error) {
      console.error("Withdraw profile fetch error:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const numericAmount = Number(amount || 0);
  const safeAmount = Number.isNaN(numericAmount) ? 0 : numericAmount;

  const availableBalance = Number(userProfile?.balance || 0);
  const lockedBalance = Number(userProfile?.lockedBalance || 0);

  const createdAt =
    userProfile?.createdAt ||
    userProfile?.registeredAt ||
    userProfile?.joinedAt ||
    userProfile?.dateCreated ||
    null;

  const earlySettlementApplies = useMemo(() => {
    if (!createdAt) {
      return true;
    }

    const joinDate = new Date(createdAt);
    const now = new Date();

    if (Number.isNaN(joinDate.getTime())) {
      return true;
    }

    const diffMs = now.getTime() - joinDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return diffDays < EARLY_WITHDRAW_DAYS;
  }, [createdAt]);

  const networkFee = useMemo(() => {
    return safeAmount > 0 ? (safeAmount * NETWORK_FEE_PERCENT) / 100 : 0;
  }, [safeAmount]);

  const earlySettlementFee = useMemo(() => {
    if (!earlySettlementApplies || safeAmount <= 0) {
      return 0;
    }

    return (safeAmount * EARLY_SETTLEMENT_FEE_PERCENT) / 100;
  }, [safeAmount, earlySettlementApplies]);

  const totalDeductions = networkFee + earlySettlementFee;

  const estimatedReceivable = useMemo(() => {
    const value = safeAmount - totalDeductions;
    return value > 0 ? value : 0;
  }, [safeAmount, totalDeductions]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || Number.isNaN(numericAmount)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Enter a valid withdrawal amount.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (safeAmount < MIN_WITHDRAW) {
      Swal.fire({
        icon: "warning",
        title: "Minimum Withdrawal",
        text: `Minimum withdrawal amount is $${MIN_WITHDRAW}.`,
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (!address.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Wallet Address Required",
        text: "Enter your USDT TRC20 wallet address.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (safeAmount > availableBalance) {
      Swal.fire({
        icon: "warning",
        title: "Insufficient Balance",
        text: "Your available balance is not enough for this withdrawal request.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (estimatedReceivable <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Request",
        text: "Estimated receivable amount must be greater than zero.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    try {
      setSubmitting(true);

      const token = getToken();

      if (!token) {
        throw new Error("Login required");
      }

      const response = await fetch(`${API_BASE_URL}/api/withdrawals/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: Number(safeAmount.toFixed(2)),
          method: NETWORK,
          walletAddress: address.trim(),
          note: `${TOKEN} ${NETWORK} withdrawal request | Requested Amount: ${safeAmount.toFixed(
            2
          )} | Network Fee: ${networkFee.toFixed(
            2
          )} | Early Settlement Fee: ${earlySettlementFee.toFixed(
            2
          )} | Total Deductions: ${totalDeductions.toFixed(
            2
          )} | Estimated Receivable: ${estimatedReceivable.toFixed(2)}`,
          requestedAmount: Number(safeAmount.toFixed(2)),
          networkFee: Number(networkFee.toFixed(2)),
          earlySettlementFee: Number(earlySettlementFee.toFixed(2)),
          totalDeductions: Number(totalDeductions.toFixed(2)),
          estimatedReceivable: Number(estimatedReceivable.toFixed(2)),
          isEarlySettlement: earlySettlementApplies,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.firstWithdrawLocked) {
          Swal.fire({
            icon: "warning",
            title: "Withdrawal Unavailable",
            text: "Withdrawal is not available for your account at this time. Please try again later.",
            confirmButtonColor: "#d4af37",
            background: "#0b0b0b",
            color: "#ffffff",
          });
          return;
        }

        throw new Error(data.message || "Withdrawal request failed");
      }

      Swal.fire({
        icon: "success",
        title: "Withdrawal Request Submitted",
        text: `Your withdrawal request has been submitted successfully. Estimated receivable: $${estimatedReceivable.toFixed(
          2
        )}.`,
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      setAmount("");
      setAddress("");
      fetchProfile();

      window.dispatchEvent(new Event("dashboardRefresh"));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Request Failed",
        text: error.message || "Something went wrong.",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="withdraw-page">
      <div className="withdraw-shell">
        <div className="withdraw-header">
          <Link to="/dashboard" className="withdraw-back">
            Back
          </Link>

          <div className="withdraw-title-block">
            <h1>Withdraw USDT</h1>
            <p>Transfer USDT to your TRC20 wallet.</p>
          </div>
        </div>

        <div className="withdraw-balance-card">
          <div className="withdraw-balance-content">
            <span>Available Balance</span>
            <strong>${loadingProfile ? "..." : availableBalance.toFixed(2)}</strong>
            <small>
              Locked Balance: ${loadingProfile ? "..." : lockedBalance.toFixed(2)}
            </small>
          </div>

          <div className="withdraw-asset-card">
            <span>{TOKEN}</span>
            <strong>{NETWORK}</strong>
          </div>
        </div>

        <div className="withdraw-details-card">
          <div className="withdraw-card-title">
            <h2>Withdrawal Details</h2>
            <p>Enter the amount and wallet address carefully.</p>
          </div>

          <div className="withdraw-limits-grid withdraw-limits-grid-three">
            <div className="withdraw-limit-item">
              <span>Minimum Withdrawal</span>
              <strong>${MIN_WITHDRAW}</strong>
            </div>

            <div className="withdraw-limit-item">
              <span>Asset</span>
              <strong>{TOKEN}</strong>
            </div>

            <div className="withdraw-limit-item">
              <span>Network</span>
              <strong>{NETWORK}</strong>
            </div>
          </div>

          <form className="withdraw-form" onSubmit={handleSubmit}>
            <div className="withdraw-field">
              <label>Amount</label>
              <div className="withdraw-input-box">
                <input
                  type="number"
                  placeholder="Enter withdrawal amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <span>{TOKEN}</span>
              </div>
            </div>

            <div className="withdraw-field">
              <label>Wallet Address</label>
              <input
                type="text"
                placeholder="Enter USDT TRC20 wallet address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <small>Only USDT TRC20 wallet addresses are supported.</small>
            </div>

            <div className="withdraw-preview-card">
              <div className="withdraw-preview-title">
                <h3>Settlement Preview</h3>
                <span>
                  {TOKEN} {NETWORK}
                </span>
              </div>

              <div className="withdraw-preview-row">
                <span>Requested Amount</span>
                <strong>${safeAmount.toFixed(2)}</strong>
              </div>

              <div className="withdraw-preview-row">
                <span>Network Fee</span>
                <strong>${networkFee.toFixed(2)}</strong>
              </div>

              <div className="withdraw-preview-row">
                <span>Early Settlement Fee</span>
                <strong>${earlySettlementFee.toFixed(2)}</strong>
              </div>

              <div className="withdraw-preview-row">
                <span>Total Deductions</span>
                <strong>${totalDeductions.toFixed(2)}</strong>
              </div>

              <div className="withdraw-preview-row withdraw-preview-final">
                <span>Estimated Receivable</span>
                <strong>${estimatedReceivable.toFixed(2)}</strong>
              </div>
            </div>

            <div className="withdraw-safety-card">
              <strong>Wallet Safety Notice</strong>
              <p>
                Please make sure your wallet address is correct. USDT sent to an
                incorrect or unsupported network address cannot be recovered.
              </p>
            </div>

            <button
              type="submit"
              className="withdraw-submit"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Withdrawal Request"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}